import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Switch,
  Typography,
  Box,
  Divider,
  TextField,
  Collapse,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import NotificationsOffIcon from "@mui/icons-material/NotificationsOff";
import AlarmIcon from "@mui/icons-material/Alarm";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import EventIcon from "@mui/icons-material/Event";
import LightModeIcon from "@mui/icons-material/LightMode";
import WavingHandIcon from "@mui/icons-material/WavingHand";
import BedtimeIcon from "@mui/icons-material/Bedtime";
import {
  getNotificationPreferences,
  setNotificationPreferences,
  DEFAULT_PREFERENCES,
} from "../../notifications/notificationPreferences";
import { rescheduleAllNotifications } from "../../notifications/rescheduleAllNotifications";

/**
 * A toggle row with icon, label, description, and switch.
 */
const SettingRow = ({
  icon,
  label,
  description,
  checked,
  onChange,
  disabled,
  indent,
}) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      py: 1,
      pl: indent ? 4 : 0,
      opacity: disabled ? 0.45 : 1,
      transition: "opacity 0.2s ease",
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", flex: 1, mr: 2 }}>
      {icon && (
        <Box
          sx={{
            mr: 1.5,
            display: "flex",
            color: disabled ? "text.disabled" : "primary.main",
          }}
        >
          {icon}
        </Box>
      )}
      <Box>
        <Typography
          variant="body1"
          sx={{ fontWeight: indent ? 400 : 600, fontSize: indent ? 14 : 15 }}
        >
          {label}
        </Typography>
        {description && (
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", fontSize: 12, mt: 0.25 }}
          >
            {description}
          </Typography>
        )}
      </Box>
    </Box>
    <Switch
      checked={checked}
      onChange={onChange}
      disabled={disabled}
      color="primary"
    />
  </Box>
);

const NotificationSettingsDialog = ({ open, onClose }) => {
  const [prefs, setPrefs] = useState(DEFAULT_PREFERENCES);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Load preferences when dialog opens
  useEffect(() => {
    if (open) {
      setLoading(true);
      getNotificationPreferences().then((savedPrefs) => {
        setPrefs(savedPrefs);
        setLoading(false);
      });
    }
  }, [open]);

  const updateTaskReminders = (key, value) => {
    setPrefs((prev) => ({
      ...prev,
      taskReminders: { ...prev.taskReminders, [key]: value },
    }));
  };

  const updateEngagement = (key, value) => {
    setPrefs((prev) => ({
      ...prev,
      engagement: { ...prev.engagement, [key]: value },
    }));
  };

  const updateQuietHours = (key, value) => {
    setPrefs((prev) => ({
      ...prev,
      quietHours: { ...prev.quietHours, [key]: value },
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await setNotificationPreferences(prefs);
      // Re-schedule all notifications based on new preferences
      await rescheduleAllNotifications();
      onClose();
    } catch (error) {
      console.error("Failed to save notification preferences:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setPrefs({ ...DEFAULT_PREFERENCES });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={{ zIndex: 1301 }} // Nested — above PreferencesDialog (MUI default 1300)
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #197b68 0%, #004a3c 100%)",
          py: 3,
          px: 3,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <NotificationsIcon sx={{ color: "#fff", fontSize: 28 }} />
        <DialogTitle
          sx={{
            p: 0,
            color: "#fff",
            fontWeight: 700,
            fontSize: 20,
          }}
        >
          Notification Settings
        </DialogTitle>
      </Box>

      <DialogContent sx={{ px: 3, py: 2 }}>
        {loading ? (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <Typography>Loading preferences...</Typography>
          </Box>
        ) : (
          <>
            {/* ── Task Reminders ── */}
            <Box sx={{ mb: 1 }}>
              <SettingRow
                icon={<NotificationsIcon />}
                label="Task Reminders"
                description="Notifications related to your task deadlines and schedules"
                checked={prefs.taskReminders.enabled}
                onChange={(e) =>
                  updateTaskReminders("enabled", e.target.checked)
                }
              />
              <Collapse in={prefs.taskReminders.enabled}>
                <SettingRow
                  icon={<AlarmIcon fontSize="small" />}
                  label="Deadline Approaching"
                  description="Get notified when a task's deadline is getting close"
                  checked={prefs.taskReminders.deadlineApproaching}
                  onChange={(e) =>
                    updateTaskReminders("deadlineApproaching", e.target.checked)
                  }
                  disabled={!prefs.taskReminders.enabled}
                  indent
                />
                <SettingRow
                  icon={<WarningAmberIcon fontSize="small" />}
                  label="Overdue"
                  description="Get notified when a task is past its deadline"
                  checked={prefs.taskReminders.overdue}
                  onChange={(e) =>
                    updateTaskReminders("overdue", e.target.checked)
                  }
                  disabled={!prefs.taskReminders.enabled}
                  indent
                />
                <SettingRow
                  icon={<EventIcon fontSize="small" />}
                  label="Scheduled Start"
                  description="Get notified when a scheduled task is due to start"
                  checked={prefs.taskReminders.scheduleStart}
                  onChange={(e) =>
                    updateTaskReminders("scheduleStart", e.target.checked)
                  }
                  disabled={!prefs.taskReminders.enabled}
                  indent
                />
              </Collapse>
            </Box>

            <Divider sx={{ my: 1.5 }} />

            {/* ── Engagement ── */}
            <Box sx={{ mb: 1 }}>
              <SettingRow
                icon={<WavingHandIcon />}
                label="Engagement"
                description="Friendly reminders to check in on your tasks"
                checked={prefs.engagement.enabled}
                onChange={(e) => updateEngagement("enabled", e.target.checked)}
              />
              <Collapse in={prefs.engagement.enabled}>
                <SettingRow
                  icon={<LightModeIcon fontSize="small" />}
                  label="Daily Agenda"
                  description="A daily nudge at midday to review your tasks"
                  checked={prefs.engagement.dailyAgenda}
                  onChange={(e) =>
                    updateEngagement("dailyAgenda", e.target.checked)
                  }
                  disabled={!prefs.engagement.enabled}
                  indent
                />
                <SettingRow
                  icon={<NotificationsOffIcon fontSize="small" />}
                  label="Comeback Reminder"
                  description="A gentle nudge if you haven't opened the app in a while"
                  checked={prefs.engagement.comeback}
                  onChange={(e) =>
                    updateEngagement("comeback", e.target.checked)
                  }
                  disabled={!prefs.engagement.enabled}
                  indent
                />
              </Collapse>
            </Box>

            <Divider sx={{ my: 1.5 }} />

            {/* ── Quiet Hours ── */}
            <Box sx={{ mb: 1 }}>
              <SettingRow
                icon={<BedtimeIcon />}
                label="Quiet Hours"
                description="Delay notifications during specific hours"
                checked={prefs.quietHours.enabled}
                onChange={(e) => updateQuietHours("enabled", e.target.checked)}
              />
              <Collapse in={prefs.quietHours.enabled}>
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    pl: 4,
                    pr: 1,
                    py: 1,
                    alignItems: "center",
                  }}
                >
                  <TextField
                    label="From"
                    type="time"
                    value={prefs.quietHours.start}
                    onChange={(e) => updateQuietHours("start", e.target.value)}
                    disabled={!prefs.quietHours.enabled}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ step: 300 }}
                    size="small"
                    sx={{ flex: 1 }}
                  />
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    to
                  </Typography>
                  <TextField
                    label="Until"
                    type="time"
                    value={prefs.quietHours.end}
                    onChange={(e) => updateQuietHours("end", e.target.value)}
                    disabled={!prefs.quietHours.enabled}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ step: 300 }}
                    size="small"
                    sx={{ flex: 1 }}
                  />
                </Box>
                <Box sx={{ pl: 4, pr: 1, pb: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      fontSize: 12,
                      fontStyle: "italic",
                    }}
                  >
                    Notifications during this window will be delayed until quiet
                    hours end.
                  </Typography>
                </Box>
              </Collapse>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: "space-between", px: 3, pb: 2 }}>
        <Button
          onClick={handleReset}
          sx={{ color: "text.secondary", textTransform: "none" }}
          disabled={saving}
        >
          Reset to defaults
        </Button>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            onClick={onClose}
            sx={{ textTransform: "none" }}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving || loading}
            sx={{
              borderRadius: 2,
              px: 3,
              textTransform: "none",
              background: "linear-gradient(135deg, #197b68 0%, #004a3c 100%)",
              "&:hover": {
                background: "linear-gradient(135deg, #0b6453 0%, #002a1c 100%)",
              },
            }}
          >
            {saving ? "Saving..." : "Save"}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default NotificationSettingsDialog;
